<script setup lang="ts">
import { computed, onMounted, ref } from "vue"
import { Icon } from "@/components/ui"

type StepState = "INCOMPLETE" | "COMPLETE"

export interface Step {
  key: string | symbol
  label: string
  state: StepState
  disabled?: boolean
}

interface StepperProps {
  steps: Step[]
  initialStep?: string | symbol
  horizontal?: boolean
}

defineOptions({
  inheritAttrs: false,
})

const { steps, initialStep, horizontal = false } = defineProps<StepperProps>()

const currentStep = ref<string | symbol | undefined>()

onMounted(() => {
  currentStep.value = initialStep ?? steps[0].key
})

function makeActive(key: string | symbol) {
  currentStep.value = key
}

const canNext = computed(() => {
  const currentStepIndex = steps.findIndex(step => step.key === currentStep.value)
  if (currentStepIndex < 0 || currentStepIndex >= steps.length - 1)
    return false
  const nextStep = steps[currentStepIndex + 1]
  return nextStep.state === "COMPLETE" || !nextStep.disabled
})

const canPrevious = computed(() => {
  const currentStepIndex = steps.findIndex(step => step.key === currentStep.value)
  if (currentStepIndex <= 0)
    return false
  const prevStep = steps[currentStepIndex - 1]
  return prevStep.state === "COMPLETE" || !prevStep.disabled
})

function next() {
  if (!canNext.value)
    return
  const currentStepIndex = steps.findIndex(step => step.key === currentStep.value)
  const nextStep = steps[currentStepIndex + 1]
  currentStep.value = nextStep.key
}

function previous() {
  const currentStepIndex = steps.findIndex(step => step.key === currentStep.value)
  if (!canPrevious.value)
    return
  const prevStep = steps[currentStepIndex - 1]
  currentStep.value = prevStep.key
}

defineExpose({ next, previous, canNext, canPrevious })
</script>

<template>
  <div :class="horizontal ? 'flex flex-col' : 'block md:flex'">
    <div :class="[horizontal ? 'w-full mb-8' : 'mr-0 mb-8 md:mb-0 md:mr-8 shrink-0']">
      <div :class="[horizontal ? 'flex items-start justify-center px-4' : 'block']">
        <div v-for="(step, index) in steps" :key="index" :class="[horizontal ? 'flex items-top flex-1 max-w-48' : 'block']">
          <!-- step button -->
          <button
            class="font-comfortaa font-bold group"
            :class="[
              horizontal ? 'flex flex-col items-center w-full' : 'flex items-center',
              step.key === currentStep ? 'text-[#333]' : 'text-[#9b9b9b]',
              step.disabled ? 'cursor-not-allowed' : 'cursor-pointer',
            ]"
            :disabled="step.disabled"
            @click="() => makeActive(step.key)"
          >
            <span
              class="flex items-center justify-center rounded-full w-8 h-8 border-2 transition-colors"
              :class="[
                horizontal ? 'mb-2' : 'mr-4',
                {
                  'border-nav-active bg-nav-active text-white ': steps.findIndex(s => s.key === currentStep) > index,
                  'border-white bg-nav-active': step.state === 'COMPLETE',
                  'border-[#333]': step.state === 'INCOMPLETE' && step.key === currentStep,
                  'border-[#9b9b9b]': step.state === 'INCOMPLETE' && step.key !== currentStep,
                  'group-hover:border-[#333]': !step.disabled && step.state === 'INCOMPLETE',
                },
              ]"
            >
              <span v-if="step.state === 'COMPLETE'" data-test="step-complete-icon">
                <Icon icon="Check" size="sm" color="white" class="fill-white" />
              </span>
              <span v-else :class="[horizontal ? 'text-xs sm:text-sm font-bold' : 'font-bold']">
                {{ index + 1 }}
              </span>
            </span>
            <!-- step label -->
            <span :class="[horizontal ? 'text-xs sm:text-sm text-center leading-tight px-1 lg:whitespace-nowrap' : 'text-base']">
              {{ step.label }}
            </span>
          </button>
          <!-- step divider -->
          <div
            v-if="index !== steps.length - 1"
            :class="[
              horizontal ? 'flex-1 h-0.5 min-w-5 md:min-w-16 mt-3' : 'border-l-2 h-5 md:h-16 my-2 md:my-3.5 ml-4',
              {
                'bg-[#333]': step.state === 'COMPLETE' && horizontal,
                'bg-[#9b9b9b]': step.state !== 'COMPLETE' && horizontal,
                'border-[#333]': step.state === 'COMPLETE' && !horizontal,
                'border-[#9b9b9b]': step.state !== 'COMPLETE' && !horizontal,
              },
            ]"
          />
        </div>
      </div>
    </div>

    <!-- step content -->
    <div class="w-full">
      <div v-for="(step, index) in steps" :key="`step-slot-${index}`">
        <slot v-if="step.key === currentStep" :name="`step-${index + 1}`" v-bind="{ step }" />
      </div>
    </div>
  </div>
</template>
